import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import InvestorPortalLayout from '../../../components/market/InvestorPortalLayout';
import { useInvestorAuth } from '../../../context/InvestorAuthContext';
import { Icon } from '../../../utils/icons';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const fmtUpb = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

export default function InvestorPortalPools() {
  const { token } = useInvestorAuth();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/market/pools`, { headers: { Authorization: `Bearer ${token}` } });
        if (!mounted) return;
        setPools(r.data?.pools || []);
      } catch {
        // 401/403 fall through to the empty state — auth issues are
        // surfaced by InvestorPortalLayout.
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  return (
    <InvestorPortalLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111111' }}>Available Pools</h1>
        <p style={{ margin: '4px 0 22px', fontSize: 13, color: '#6b7280' }}>
          Active and upcoming Doorly mortgage loan pools.
        </p>

        {loading ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading pools…</div>
        ) : pools.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 40, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 12px', borderRadius: 12, background: '#eff3fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="chart" size={26} color="#3a6ad9" />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111111', marginBottom: 4 }}>No active pools</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Doorly's investor pools will be published soon. You'll be emailed when new pools go live.
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {pools.map((p) => (
              <Link key={p.id} to={`/market/pools/${p.id}`}
                style={{ display: 'block', textDecoration: 'none', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#111111' }}>{p.pool_name || p.pool_number}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{p.pool_number}</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#3a6ad9', background: '#eff3fe', padding: '4px 10px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {p.status === 'published' ? 'Open' : (p.status || 'Active').replace('_', ' ')}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, fontSize: 12 }}>
                  <Stat label="Loans" value={p.loan_count ?? '—'} />
                  <Stat label="UPB" value={fmtUpb(p.total_upb)} />
                  <Stat label="WA Note Rate" value={p.wa_note_rate != null ? `${Number(p.wa_note_rate).toFixed(2)}%` : '—'} />
                  <Stat label="WA FICO" value={p.wa_fico ?? '—'} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </InvestorPortalLayout>
  );
}

const Stat = ({ label, value }) => (
  <div style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 10px' }}>
    <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 700, color: '#111111' }}>{value}</div>
  </div>
);
